/**
 * PC端环境检测模块
 * 提供与移动端 ~env@1.0 一致的API接口
 * @module env/1.0
 * @author PLC
 * @version 1.0
 */

/**
 * 获取URL参数
 */
function getUrlParams() {
    const params = {}
    const search = window.location.search.substring(1)
    if (search) {
        const pairs = search.split('&')
        pairs.forEach(pair => {
            const [key, value] = pair.split('=')
            params[decodeURIComponent(key)] = decodeURIComponent(value || '')
        })
    }
    return params
}

/**
 * 解析UserAgent
 */
function parseUserAgent() {
    const ua = navigator.userAgent.toLowerCase()
    const urlParams = getUrlParams()

    // 检测是否在分期乐App内
    const app_patt = /fenqile_(ios|android)_(\d{1,}\.\d{1,}\.\d{1,})/i
    const app_android_patt = /fenqile_android_(\d{1,}\.\d{1,}\.\d{1,})/i
    const app_ios_patt = /fenqile_ios_(\d{1,}\.\d{1,}\.\d{1,})/i

    const isApp = app_patt.test(ua) || urlParams.isApp === '1'
    const isAndroid = app_android_patt.test(ua)
    const isIOS = app_ios_patt.test(ua)

    // 提取App版本号
    let appVersion = ''
    const match = ua.match(app_patt)
    if (match && match[2]) {
        appVersion = match[2]
    }

    return {
        isApp,
        isAndroid,
        isIOS,
        appVersion,
        userAgent: ua
    }
}

/**
 * 解析平台信息
 */
function getPlatform() {
    const uaInfo = parseUserAgent()

    if (uaInfo.isIOS) {
        return 'iOS'
    } else if (uaInfo.isAndroid) {
        return 'android'
    } else {
        return 'web'
    }
}

/**
 * 检测是否在微信中
 */
function isWechat() {
    const ua = navigator.userAgent.toLowerCase()
    return ua.includes('micromessenger')
}

/**
 * 检测是否在微信小程序中
 */
function isWebInMp() {
    const ua = navigator.userAgent.toLowerCase()
    return ua.includes('miniprogram') || window.__wxjs_environment === 'miniprogram'
}

// 初始化环境信息
const uaInfo = parseUserAgent()

/**
 * 环境变量对象
 * 提供与移动端 ~env@1.0 一致的API
 */
const M_ENV = {
    /**
     * 当前运行平台 "iOS", "android" or "web"
     */
    platform: getPlatform(),

    /**
     * UserAgent字符串
     */
    userAgent: navigator.userAgent,

    /**
     * App版本号
     */
    appVersion: uaInfo.appVersion,

    /**
     * App名称
     */
    appName: uaInfo.isApp ? 'fenqile' : '',

    /**
     * 设备像素比
     */
    dpr: window.devicePixelRatio || 1,
    scale: window.devicePixelRatio || 1,

    /**
     * 状态栏高度
     * 从App注入的weex.config.StatusBarHeight或window对象获取
     * 在App环境：App会注入状态栏高度（单位：px）
     * 在纯Web环境：返回0
     */
    get statusBarHeight() {
        // 如果不在App环境，直接返回0
        if (!uaInfo.isApp) {
            return 0
        }

        // 尝试从weex.config.StatusBarHeight获取（兼容移动端注入方式）
        if (typeof weex !== 'undefined' && weex?.config?.StatusBarHeight) {
            return weex.config.StatusBarHeight
        }

        // 尝试从window对象获取（WebView注入方式）
        if (window.StatusBarHeight) {
            return window.StatusBarHeight
        }

        // 尝试从window.__FQL_ENV__获取（通用注入方式）
        if (window.__FQL_ENV__?.statusBarHeight) {
            return window.__FQL_ENV__.statusBarHeight
        }

        // 如果在App环境但未获取到注入值，返回默认值
        // iOS刘海屏：44px，普通iOS：20px，Android：24px
        if (uaInfo.isIOS) {
            // 通过device model判断是否刘海屏
            const isNotch = /iPhone (X|1[1-4])/.test(navigator.userAgent) ||
                            window.screen.height >= 812 // 刘海屏高度判断
            return isNotch ? 44 : 20
        } else if (uaInfo.isAndroid) {
            return 24
        }

        return 0
    },

    /**
     * 设备宽度
     */
    deviceWidth: window.screen.width,

    /**
     * 设备高度
     */
    deviceHeight: window.screen.height,

    /**
     * 是否为原生App环境（iOS或Android原生）
     */
    isNative: false,

    /**
     * 是否为原生iOS环境
     */
    isNativeInIOS: uaInfo.isIOS,

    /**
     * 是否为原生Android环境
     */
    isNativeInAndroid: uaInfo.isAndroid,

    /**
     * 是否为原生App环境（iOS或Android）
     */
    get isNativeInApp() {
        return this.isNativeInIOS || this.isNativeInAndroid
    },

    /**
     * 是否为Web环境
     */
    isWeb: !uaInfo.isApp,

    /**
     * 是否为Web在App内（H5页面在App的WebView中）
     */
    get isWebInApp() {
        // 通过UA或URL参数判断是否在App的WebView中
        return uaInfo.isApp && this.isWeb
    },

    /**
     * 是否为Web在微信中
     */
    get isWebInWechat() {
        return this.isWeb && isWechat()
    },

    /**
     * 是否为Web在小程序中
     */
    get isWebInMp() {
        return this.isWeb && isWebInMp()
    },

    /**
     * 是否为移动端（手机浏览器）
     */
    get isMobile() {
        const ua = navigator.userAgent.toLowerCase()
        return /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua)
    },

    /**
     * 是否为PC端
     */
    get isPC() {
        return !this.isMobile
    },

    /**
     * 是否为刘海屏iPhone（PC端固定为false）
     */
    isIphoneHair: false,

    /**
     * 是否为鸿蒙系统
     */
    isHarmony: false
}

// 兼容移动端的常用属性别名
M_ENV.is_native_in_app = M_ENV.isNativeInApp
M_ENV.is_native_in_ios = M_ENV.isNativeInIOS
M_ENV.is_native_in_android = M_ENV.isNativeInAndroid
M_ENV.is_web_in_app = M_ENV.isWebInApp

export default M_ENV